import ProfileReminder from './ProfileReminder';
import SiteFooter from './SiteFooter';
import { isNativeApp } from '../utils/platform';
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBadges } from '../context/BadgeContext';
import SubscriptionReminder from './SubscriptionReminder';
import { useDarkMode } from '../hooks/useDarkMode';
import useFaviconBadge from '../hooks/useFaviconBadge';
import { useInstallPrompt } from '../hooks/useInstallPrompt';
import api, { getPlatformSettings } from '../utils/api';
import { FiHome, FiLearn, FiChat, FiBell, FiDashboard, FiGroup, FiUsers, FiBroadcast, FiNewspaper, FiProfile } from './NavIcons';
import {
  IcDashboard, IcFeed, IcCourses, IcAssign, IcAssess, IcQA,
  IcQuiz, IcFlash, IcX, IcMsg, IcGroup, IcBell, IcProfile,
  IcSettings, IcLogout, IcTrophy, IcAnnounce, IcTimetable,
  IcPastQ, IcSub, IcId, IcCert,
  IcMoney, IcChart, IcHistory, IcExport, IcCalendar, IcCamera,
  IcAdmin, IcFilter, IcMenuHamburger, IcTrash,
  IcUsers, IcGradCap, IcLayers, IcSliders, IcBroadcast, IcNewspaper, IcMail,
  IcUpload,
} from './Icons';

const STUDENT_NAV = [
  { section: 'Home' },
  { id: 'feed',          label: 'News Feed',       path: '/feed',          Icon: IcFeed      },
  { id: 'dashboard',     label: 'Dashboard',       path: '/dashboard',     Icon: IcDashboard },
  { section: 'Learn', hub: '/learn' },
  { id: 'my-courses',    label: 'My Courses',      path: '/my-courses',    Icon: IcCourses   },
  { id: 'assignments',   label: 'Assignments',     path: '/assignments',   Icon: IcAssign, badge: 'assign' },
  { id: 'assessments',   label: 'Assessments',     path: '/assessments',   Icon: IcAssess    },
  { id: 'quiz',          label: 'Take Quiz',       path: '/quiz',          Icon: IcQuiz      },
  { id: 'past-questions',label: 'Library',         path: '/past-questions',Icon: IcPastQ     },
  { id: 'flashcards',    label: 'Flashcards',      path: '/flashcards',    Icon: IcFlash     },
  { id: 'qa-forum',      label: 'Q&A Forum',       path: '/qa-forum',      Icon: IcQA        },
  { id: 'timetable',     label: 'Timetable',       path: '/timetable',     Icon: IcTimetable },
  { id: 'planner',       label: 'Study Planner',   path: '/planner',       Icon: IcCalendar  },
  { section: 'Progress', hub: '/progress' },
  { id: 'my-results',    label: 'My Results',      path: '/my-results',    Icon: IcTrophy    },
  { id: 'cgpa-tracker',  label: 'CGPA Tracker',    path: '/cgpa-tracker',  Icon: IcChart     },
  { id: 'progress-reports', label: 'Progress Reports', path: '/progress-reports', Icon: IcAssess },
  { id: 'announcements', label: 'Announcements',   path: '/announcements', Icon: IcAnnounce  },
  { section: 'Community' },
  { id: 'messages',      label: 'Messages',        path: '/messages',      Icon: IcMsg, badge: 'messages' },
  { id: 'groups',        label: 'Study Groups',    path: '/groups',        Icon: IcGroup     },
  { id: 'study-buddies', label: 'Study Buddies',   path: '/study-buddies', Icon: IcUsers     },
  { id: 'photos',        label: 'Photo Gallery',   path: '/photos',        Icon: IcCamera    },
  { id: 'notifications', label: 'Notifications',   path: '/notifications', Icon: IcBell, badge: 'notifs' },
  { section: 'Account' },
  { id: 'saved',         label: 'Saved',           path: '/saved',         Icon: IcLayers    },
  { id: 'membership',    label: 'Membership',      path: '/membership',    Icon: IcSub       },
  { id: 'account',       label: 'Account',         path: '/account',       Icon: IcProfile   },
];

const TUTOR_NAV = [
  { section: 'Main' },
  { id: 'feed',           label: 'News Feed',        path: '/feed',           Icon: IcFeed      },
  { id: 'tutor-dashboard',label: 'Dashboard',        path: '/tutor-dashboard',Icon: IcDashboard },
  { section: 'Teaching' },
  { id: 'course-manager', label: 'Course Manager',   path: '/course-manager', Icon: IcCourses   },
  { id: 'qa-forum',       label: 'Q&A Forum',        path: '/qa-forum',       Icon: IcQA        },
  { section: 'Community' },
  { id: 'messages',       label: 'Messages',         path: '/messages',       Icon: IcMsg, badge: 'messages' },
  { id: 'groups',         label: 'Study Groups',     path: '/groups',         Icon: IcGroup     },
  { id: 'notifications',  label: 'Notifications',    path: '/notifications',  Icon: IcBell, badge: 'notifs' },
  { section: 'Account' },
  { id: 'account',        label: 'Account',          path: '/account',        Icon: IcProfile   },
];

const WRITER_NAV = [
  { section: 'Writer Studio' },
  { id: 'news-write', label: 'Write Article', path: '/news/write', Icon: IcNewspaper },
  { id: 'news',       label: 'View News Hub', path: '/news',       Icon: IcFeed      },
  { section: 'Account' },
  { id: 'account',    label: 'Account',       path: '/account',    Icon: IcProfile   },
];

const SECRETARY_NAV = [
  { section: 'Desk' },
  { id: 'sec-desk',    label: 'Secretary Desk', path: '/secretary', Icon: IcDashboard },
  { section: 'Account' },
  { id: 'account',     label: 'Account',        path: '/account',   Icon: IcProfile   },
];

const LOADER_NAV = [
  { section: 'Loader' },
  { id: 'loader-console', label: 'Question Loader', path: '/loader-console', Icon: IcUpload },
  { section: 'Account' },
  { id: 'account',  label: 'Account',  path: '/account',  Icon: IcProfile  },
];

const JAMB_NAV = [
  { section: 'Main' },
  { id: 'jamb-feed',         label: 'News Feed',       path: '/feed',              Icon: IcFeed      },
  { id: 'jamb-dashboard',    label: 'Dashboard',       path: '/jamb/dashboard',    Icon: IcDashboard },
  { section: 'Preparation' },
  { id: 'jamb-quiz',         label: 'Practice Quiz',   path: '/jamb/quiz',         Icon: IcQuiz      },
  { id: 'jamb-exam',         label: 'Mock Exam (CBT)', path: '/jamb/exam',         Icon: IcAssess    },
  { id: 'jamb-cas',          label: 'Assessments',     path: '/jamb/cas',          Icon: IcAssess    },
  { id: 'jamb-results',      label: 'My Results',      path: '/jamb/results',      Icon: IcChart     },
  { id: 'jamb-assignments',  label: 'Assignments',     path: '/jamb/assignments',  Icon: IcAssign    },
  { id: 'jamb-flashcards',   label: 'Flashcards',      path: '/jamb/flashcards',   Icon: IcFlash     },
  { id: 'planner',           label: 'Study Planner',   path: '/planner',           Icon: IcCalendar  },
  { section: 'Admission Tools' },
  { id: 'jamb-cutoffs',      label: 'Cutoffs & Advisor', path: '/jamb/cutoffs',    Icon: IcChart     },
  { id: 'jamb-finder',       label: 'School Finder',   path: '/jamb/finder',       Icon: IcGradCap   },
  { id: 'jamb-subjects',     label: 'Subject Checker', path: '/jamb/subjects',     Icon: IcPastQ     },
  { id: 'jamb-aggregate',    label: 'Aggregate Calc',  path: '/jamb/aggregate',    Icon: IcQuiz      },
  { section: 'Community' },
  { id: 'messages',          label: 'Messages',        path: '/messages',          Icon: IcMsg, badge: 'messages' },
  { id: 'groups',            label: 'Study Groups',    path: '/groups',            Icon: IcGroup     },
  { id: 'notifications',     label: 'Notifications',   path: '/notifications',     Icon: IcBell, badge: 'notifs' },
  { section: 'Account' },
  { id: 'jamb-profile',      label: 'Profile',         path: '/jamb/profile',      Icon: IcProfile   },
  { id: 'jamb-settings',     label: 'Settings',        path: '/jamb/settings',     Icon: IcSettings  },
];

const ADMIN_NAV = [
  // Consolidated to ~12 hubs. Each hub pockets several related pages behind
  // in-page tabs (see TabbedAdmin in AdminPortal). Legacy deep-link paths
  // still resolve, so old links and bookmarks keep working.
  { section: 'Manage' },
  { id: 'admin',               label: 'Dashboard',          path: '/admin',               Icon: IcDashboard },
  { id: 'admin-insights',      label: 'Insights',           path: '/admin/insights',      Icon: IcChart     },
  { id: 'admin-students',      label: 'Students',           path: '/admin/students-hub',  Icon: IcUsers, badge: 'enrRequests' },
  { id: 'admin-tutors',        label: 'Tutors',             path: '/admin/tutors-hub',    Icon: IcGradCap   },
  { id: 'admin-records',       label: 'Academic Records',   path: '/admin/records',       Icon: IcTrophy    },
  { id: 'admin-courses',       label: 'Courses',            path: '/admin/courses-hub',   Icon: IcCourses   },
  { id: 'admin-library',       label: 'Library',            path: '/admin/past-questions',Icon: IcPastQ     },

  { section: 'Engage' },
  { id: 'admin-community',     label: 'Community',          path: '/admin/community',     Icon: IcQA        },
  { id: 'admin-comms',         label: 'Communications',     path: '/admin/comms',         Icon: IcBroadcast },
  { id: 'admin-content',       label: 'Content & Media',    path: '/admin/content-hub',   Icon: IcNewspaper },

  { section: 'Business' },
  { id: 'admin-payments',      label: 'Finances',           path: '/admin/payments',      Icon: IcMoney     },
  { id: 'admin-jamb',          label: 'JAMB / PUTME',       path: '/admin/jamb-hub',      Icon: IcGradCap   },
];

export function PlatformLogo({ size = 38, style = {} }) {
  const [logoUrl, setLogoUrl] = useState(null);
  useEffect(() => {
    getPlatformSettings().then(s => { if (s?.logoUrl) setLogoUrl(s.logoUrl); }).catch(() => {});
  }, []);
  if (logoUrl) {
    return <img src={logoUrl} alt="Toppers Tutorial" style={{ width: size, height: size, borderRadius: 10, objectFit: 'contain', flexShrink: 0, ...style }} />;
  }
  return (
    <div style={{ width: size, height: size, background: 'var(--org)', color: '#fff', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--display)', fontSize: Math.round(size * 0.5), fontWeight: 700, flexShrink: 0, ...style }}>T</div>
  );
}

const ChevronIcon = ({ down }) => (
  <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" style={{ transition: 'transform 0.18s ease', transform: down ? 'rotate(0deg)' : 'rotate(-90deg)', flexShrink: 0 }}>
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);
const MoonIcon = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
);
const SunIcon = () => (
  <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
);

// Role → nav items, shared by the app Sidebar and the website header so the
// two surfaces can never drift apart.
function useNavItems() {
  const { currentUser, isStudent, isAdmin, isWriter, isLoader, isJambStudent } = useAuth();
  const [feedEnabled, setFeedEnabled] = useState(false);
  useEffect(() => {
    getPlatformSettings().then(s => setFeedEnabled(s?.newsFeedEnabled === true)).catch(() => {});
  }, []);
  const FEED_IDS = new Set(['feed', 'jamb-feed']);
  const isSecretary = currentUser?.role === 'secretary';
  let baseNav = isAdmin ? ADMIN_NAV : isSecretary ? SECRETARY_NAV : isWriter ? WRITER_NAV : isLoader ? LOADER_NAV : isJambStudent ? JAMB_NAV : isStudent ? STUDENT_NAV : TUTOR_NAV;
  // 200L/300L students get a "Peer Courses" entry (their free peer-led track).
  if (isStudent && !isJambStudent && ['200L', '300L'].includes(currentUser?.level)) {
    const idx = baseNav.findIndex(i => i.id === 'quiz');
    baseNav = idx === -1 ? baseNav : [
      ...baseNav.slice(0, idx + 1),
      { id: 'peer-courses', label: 'Peer Courses', path: '/peer-courses', Icon: IcCourses },
      ...baseNav.slice(idx + 1),
    ];
  }
  return feedEnabled ? baseNav : baseNav.filter(item => !item.id || !FEED_IDS.has(item.id));
}

export function Sidebar({ badges = {}, mobileOpen = false, onClose }) {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { currentUser, displayName, avatarInitials, logout, isStudent, isAdmin, isWriter, isLoader, isJambStudent } = useAuth();
  const { dark, toggle: toggleDark } = useDarkMode();
  const [q, setQ] = useState('');
  const isSecretary = currentUser?.role === 'secretary';
  const navItems = useNavItems();
  const collapsibleSections = new Set(['Learn', 'Progress', 'Academic', 'Library', 'Community', 'Account', 'Teaching',
    'Preparation', 'Student Manager', 'Tutor Manager', 'Course Manager', 'Platform Manager', 'JAMB / PUTME']);
  // hub landing page per section (tapping the header opens it instead of only toggling).
  const sectionHub = {};
  navItems.forEach(item => { if (item.section && item.hub) sectionHub[item.section] = item.hub; });
  // Default: collapsible sections start CLOSED so the menu opens compact
  // (only Home / non-collapsible groups expanded) — the anti-crowding fix.
  const [openSections, setOpenSections] = useState(() => {
    const open = new Set();
    navItems.forEach(item => { if (item.section && !collapsibleSections.has(item.section)) open.add(item.section); });
    return open;
  });
  const toggleSection = (s) => {
    if (!collapsibleSections.has(s)) return;
    setOpenSections(prev => { const next = new Set(prev); next.has(s) ? next.delete(s) : next.add(s); return next; });
  };
  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };
  const matches = (label) => !q.trim() || (label || '').toLowerCase().includes(q.trim().toLowerCase());
  // When searching, only show section headers that have at least one matching item.
  const sectionHasMatch = {};
  {
    let sec = null;
    navItems.forEach(item => {
      if (item.section) { sec = item.section; if (!(sec in sectionHasMatch)) sectionHasMatch[sec] = false; }
      else if (sec != null && matches(item.label)) sectionHasMatch[sec] = true;
    });
  }
  let currentSection = null;
  return (
    <aside className={`sidebar${mobileOpen ? ' sidebar-mobile-open' : ''}${isAdmin ? ' sidebar--admin' : ''}`}>
      <div className="sidebar-brand" onClick={() => { navigate(isAdmin ? '/admin' : '/'); if (onClose) onClose(); }}>
        <PlatformLogo size={38} />
        <div>
          <div className="sidebar-brand-name">Toppers Tutorial</div>
          <div className="sidebar-brand-tag">{isAdmin ? 'Admin Portal' : 'Awakening the Excellence'}</div>
        </div>
      </div>
      <div className="sidebar-search">
        <div className="sidebar-search-wrap">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="sidebar-search-icon">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            className="sidebar-search-input"
            type="text"
            placeholder="Search..."
            value={q}
            onChange={e => setQ(e.target.value)}
          />
        </div>
      </div>
      <nav className="sidebar-nav">
        {navItems.map((item, i) => {
          if (item.section) {
            currentSection = item.section;
            if (q.trim() && !sectionHasMatch[item.section]) return null;
            const isCollapsible = collapsibleSections.has(item.section);
            const isOpen = openSections.has(item.section);
            const hubPath = sectionHub[item.section];
            return isCollapsible ? (
              <div key={i} style={{ display: 'flex', alignItems: 'center', width: '100%', padding: '12px 10px 5px' }}>
                {hubPath ? (
                  <button onClick={() => { navigate(hubPath); if (onClose) onClose(); }}
                    style={{ flex: 1, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 9, fontWeight: 700, letterSpacing: '1.4px', textTransform: 'uppercase', color: isActive(hubPath) ? 'var(--org)' : '#94a3b8', padding: 0 }}>
                    {item.section}
                  </button>
                ) : (
                  <span style={{ flex: 1, fontSize: 9, fontWeight: 700, letterSpacing: '1.4px', textTransform: 'uppercase', color: '#94a3b8' }}>{item.section}</span>
                )}
                <button onClick={() => toggleSection(item.section)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', display: 'flex', padding: 2 }}>
                  <ChevronIcon down={isOpen} />
                </button>
              </div>
            ) : (
              <div key={i} className="sidebar-section-label">{item.section}</div>
            );
          }
          if (!matches(item.label)) return null;
          if (!q.trim() && !openSections.has(currentSection)) return null;
          return (
            <button key={item.id} className={`sidebar-item${isActive(item.path) ? ' active' : ''}`}
              onClick={() => { navigate(item.path); if (onClose) onClose(); }}>
              <item.Icon size={15} className="sidebar-item-icon" />
              <span className="sidebar-item-label">{item.label}</span>
              {item.badge && badges[item.badge] > 0 && <span className="sidebar-badge">{badges[item.badge]}</span>}
            </button>
          );
        })}
      </nav>
      <div className="sidebar-user">
        <div className="sidebar-user-av" style={{ cursor: 'pointer' }} onClick={() => { navigate('/account'); if (onClose) onClose(); }}>
          {currentUser?.profilePicture ? <img src={currentUser.profilePicture} alt={displayName} /> : avatarInitials}
        </div>
        <div className="sidebar-user-info">
          <div className="sidebar-user-name">{displayName}</div>
          <div className="sidebar-user-role">
            {isAdmin ? 'Administrator' : isSecretary ? 'Secretary' : isLoader ? 'Question Loader' : isJambStudent ? 'JAMB Student' : isStudent ? 'Student' : isWriter ? 'Writer' : 'Tutor'}
            {isStudent && !isJambStudent && currentUser?.tutorialId ? ` · ${currentUser.tutorialId}` : ''}
          </div>
        </div>
        <button onClick={toggleDark} title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
          style={{ background: '#f1f5f9', border: '1px solid #e5e7eb', borderRadius: 'var(--radius-sm)', padding: 6, cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.18s ease', flexShrink: 0 }}
          onMouseEnter={e => { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.color = '#0f172a'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#64748b'; }}>
          {dark ? <SunIcon /> : <MoonIcon />}
        </button>
        <button className="sidebar-logout" title="Sign out" onClick={() => { logout(); navigate('/'); }}>
          <IcLogout size={14} />
        </button>
      </div>
    </aside>
  );
}

function GlobalSearch() {
  const navigate = useNavigate();
  const [q, setQ]               = useState('');
  const [results, setResults]   = useState(null);
  const [loading, setLoading]   = useState(false);
  const [open, setOpen]         = useState(false);
  const [mobileExp, setMobileExp] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [ddRect, setDdRect]     = useState({ top: 0, left: 0, width: 0 });
  const ref   = useRef(null);
  const mRef  = useRef(null);
  const timer = useRef(null);

  const updateRect = () => {
    if (ref.current) {
      const r = ref.current.getBoundingClientRect();
      setDdRect({ top: r.bottom + 6, left: r.left, width: r.width });
    }
  };

  useEffect(() => {
    const handler = (e) => {
      const inDesktop = ref.current && ref.current.contains(e.target);
      const inMobile  = mRef.current && mRef.current.contains(e.target);
      if (!inDesktop && !inMobile) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const controllerRef = useRef(null);

  const search = (val) => {
    setQ(val);
    clearTimeout(timer.current);
    if (controllerRef.current) { controllerRef.current.abort(); controllerRef.current = null; }
    if (!val.trim() || val.trim().length < 2) { setResults(null); setOpen(false); return; }
    const controller = new AbortController();
    controllerRef.current = controller;
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(val.trim())}`, { signal: controller.signal });
        setResults(data); setTypeFilter('all'); updateRect(); setOpen(true);
      } catch (e) {
        if (e.name !== 'CanceledError' && e.name !== 'AbortError') setResults(null);
      } finally { setLoading(false); }
    }, 350);
  };

  const goSearch = (val) => {
    if (!val.trim()) return;
    setOpen(false); setQ(''); setResults(null); setMobileExp(false);
    navigate(`/search?q=${encodeURIComponent(val.trim())}`);
  };
  const go = (path) => { setOpen(false); setQ(''); setResults(null); setMobileExp(false); navigate(path); };
  const hasResults = results && (results.users?.length || results.posts?.length || results.flashcards?.length || results.questions?.length || results.pastQuestions?.length);

  const renderDropdownContent = () => {
    if (!hasResults) return <div style={{ padding: '16px', textAlign: 'center', fontSize: 13, color: 'var(--txt-l)' }}>No results for "{q}"</div>;
    const chips = [
      { key: 'all', label: 'All' },
      results.users?.length        > 0 && { key: 'people',     label: `People (${results.users.length})` },
      results.posts?.length        > 0 && { key: 'posts',      label: `Posts (${results.posts.length})` },
      results.questions?.length    > 0 && { key: 'questions',  label: `Q&A (${results.questions.length})` },
      results.flashcards?.length   > 0 && { key: 'flashcards', label: `Flashcards (${results.flashcards.length})` },
      results.pastQuestions?.length > 0 && { key: 'past',      label: `Past Qs (${results.pastQuestions.length})` },
    ].filter(Boolean);
    const chipStyle = (active) => ({ padding: '3px 10px', borderRadius: 99, border: '1.5px solid', borderColor: active ? 'var(--org)' : 'var(--nat-d)', background: active ? 'var(--org-l)' : 'transparent', color: active ? 'var(--org)' : 'var(--txt-m)', fontSize: 11, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' });
    const show = (key) => typeFilter === 'all' || typeFilter === key;
    return (
      <>
        {chips.length > 1 && <div style={{ padding: '8px 14px', display: 'flex', gap: 6, flexWrap: 'wrap', borderBottom: '1px solid var(--nat-d)' }}>{chips.map(c => <button key={c.key} style={chipStyle(typeFilter === c.key)} onClick={() => setTypeFilter(c.key)}>{c.label}</button>)}</div>}
        {show('people') && results.users?.length > 0 && (
          <div><div style={{ padding: '8px 14px 4px', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--txt-l)' }}>People</div>
            {results.users.map(u => {
              const name = u.fullName || `${u.firstName || ''} ${u.lastName || ''}`.trim() || '—';
              return <div key={u._id} onClick={() => go(u.role === 'tutor' ? `/tutor/${u._id}` : `/student/${u._id}`)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', cursor: 'pointer', borderBottom: '1px solid var(--nat-s)' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--nat-s)'} onMouseLeave={e => e.currentTarget.style.background = ''}><div style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--org)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, color: '#fff', flexShrink: 0, overflow: 'hidden' }}>{u.profilePicture ? <img src={u.profilePicture} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : name[0]}</div><div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div><div style={{ fontSize: 11, color: 'var(--txt-l)' }}>{u.role === 'tutor' ? 'Tutor' : u.tutorialId} · {u.department?.slice(0, 20)}</div></div></div>;
            })}
          </div>
        )}
        {show('flashcards') && results.flashcards?.length > 0 && (
          <div><div style={{ padding: '8px 14px 4px', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--txt-l)' }}>Flashcards</div>{results.flashcards.map(f => <div key={f._id} onClick={() => go(`/flashcard/${f._id}`)} style={{ padding: '8px 14px', cursor: 'pointer', borderBottom: '1px solid var(--nat-s)' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--nat-s)'} onMouseLeave={e => e.currentTarget.style.background = ''}><div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.front}</div><div style={{ fontSize: 11, color: 'var(--txt-l)' }}>{f.course}{f.topic ? ` · ${f.topic}` : ''}</div></div>)}</div>
        )}
        {show('questions') && results.questions?.length > 0 && (
          <div><div style={{ padding: '8px 14px 4px', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--txt-l)' }}>Q&A Forum</div>{results.questions.map(q => <div key={q._id} onClick={() => go('/qa-forum')} style={{ padding: '8px 14px', cursor: 'pointer', borderBottom: '1px solid var(--nat-s)' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--nat-s)'} onMouseLeave={e => e.currentTarget.style.background = ''}><div style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{q.title || q.text?.slice(0, 60)}</div><div style={{ fontSize: 11, color: 'var(--txt-l)' }}>{q.course}</div></div>)}</div>
        )}
        {show('past') && results.pastQuestions?.length > 0 && (
          <div><div style={{ padding: '8px 14px 4px', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--txt-l)' }}>Past Questions</div>{results.pastQuestions.map(p => <div key={p._id} onClick={() => go('/past-questions')} style={{ padding: '8px 14px', cursor: 'pointer', borderBottom: '1px solid var(--nat-s)' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--nat-s)'} onMouseLeave={e => e.currentTarget.style.background = ''}><div style={{ fontSize: 13, fontWeight: 700 }}>{p.title}</div><div style={{ fontSize: 11, color: 'var(--txt-l)' }}>{p.course} · {p.year}</div></div>)}</div>
        )}
        {show('posts') && results.posts?.length > 0 && (
          <div><div style={{ padding: '8px 14px 4px', fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--txt-l)' }}>Posts</div>{results.posts.map(p => { const authorName = p.author?.fullName || `${p.author?.firstName || ''} ${p.author?.lastName || ''}`.trim() || '—'; return <div key={p._id} onClick={() => go('/feed')} style={{ padding: '8px 14px', cursor: 'pointer' }} onMouseEnter={e => e.currentTarget.style.background = 'var(--nat-s)'} onMouseLeave={e => e.currentTarget.style.background = ''}><div style={{ fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.text?.slice(0, 80)}</div><div style={{ fontSize: 11, color: 'var(--txt-l)' }}>{authorName}</div></div>; })}</div>
        )}
        <div style={{ padding: '10px 14px', borderTop: '1px solid var(--nat-d)', textAlign: 'center' }}>
          <button onClick={() => goSearch(q)} style={{ fontSize: 12, color: 'var(--org)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>See all results for "{q}"</button>
        </div>
      </>
    );
  };

  const SearchIcon = () => (<svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>);

  return (
    <>
      <div ref={ref} className="search-bar-desktop" style={{ position: 'relative', width: 300 }}>
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--txt-l)', pointerEvents: 'none', display: 'flex' }}><SearchIcon /></div>
          <input
            style={{ width: '100%', height: 34, background: 'var(--nat-s)', border: '1.5px solid var(--nat-d)', borderRadius: 99, padding: '0 12px 0 32px', fontSize: 13, fontFamily: 'var(--body)', color: 'var(--txt)', outline: 'none', boxSizing: 'border-box' }}
            placeholder="Search users, posts, flashcards..."
            value={q}
            onChange={e => search(e.target.value)}
            onFocus={() => { if (results) { updateRect(); setOpen(true); } }}
            onKeyDown={e => { if (e.key === 'Enter' && q.trim()) goSearch(q); }}
          />
          {loading && <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}><div className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /></div>}
        </div>
        {open && (
          <div style={{ position: 'fixed', top: ddRect.top, left: ddRect.left, width: ddRect.width, background: 'var(--white)', border: '1px solid var(--nat-d)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)', zIndex: 10000, maxHeight: 460, overflowY: 'auto' }}>
            {renderDropdownContent()}
          </div>
        )}
      </div>
      <button className="search-icon-mobile topbar-icon-btn" aria-label="Search" onClick={() => setMobileExp(true)}><SearchIcon /></button>
      {mobileExp && (
        <div ref={mRef} style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'var(--nat)', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', gap: 8, padding: '10px 14px', borderBottom: '1px solid var(--nat-d)' }}>
            <input autoFocus style={{ flex: 1, height: 36, background: 'var(--nat-s)', border: '1.5px solid var(--nat-d)', borderRadius: 99, padding: '0 14px', fontSize: 13, fontFamily: 'var(--body)', color: 'var(--txt)', outline: 'none' }} placeholder="Search..." value={q} onChange={e => search(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && q.trim()) goSearch(q); }} />
            <button onClick={() => { setMobileExp(false); search(''); }} style={{ background: 'none', border: 'none', fontSize: 13, color: 'var(--txt-m)', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {results && renderDropdownContent()}
            {hasResults && <div style={{ padding: '12px 14px', textAlign: 'center' }}><button onClick={() => goSearch(q)} style={{ fontSize: 13, color: 'var(--org)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>See all results for "{q}"</button></div>}
          </div>
        </div>
      )}
    </>
  );
}

// ── Website header ──────────────────────────────────────────────────────────
// The browser surface gets a conventional site navbar (logo · links · search ·
// bell · avatar menu) instead of the app's sidebar+topbar chrome. Same 64px
// height as the Topbar so full-height pages (chat) keep their math.
const PRIMARY_LINK_IDS = [
  'feed', 'my-courses', 'quiz', 'messages', 'groups',
  'tutor-dashboard', 'course-manager',
  'jamb-feed', 'jamb-dashboard', 'jamb-quiz', 'jamb-exam',
  'admin', 'admin-insights', 'admin-students', 'admin-courses', 'admin-payments',
  'news-write', 'news', 'sec-desk', 'loader-console',
];

export function WebHeader({ title, actions, badges = {}, onMenuToggle }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, displayName, avatarInitials, logout, isAdmin, isWriter } = useAuth();
  const { dark, toggle: toggleDark } = useDarkMode();
  const navItems = useNavItems();
  const [moreOpen, setMoreOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const moreRef = useRef(null);
  const userRef = useRef(null);

  useEffect(() => {
    const close = e => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
      if (userRef.current && !userRef.current.contains(e.target)) setUserOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const linkItems  = navItems.filter(i => !i.section);
  const primary    = linkItems.filter(i => PRIMARY_LINK_IDS.includes(i.id)).slice(0, 6);
  const primaryIds = new Set(primary.map(i => i.id));
  const isActive   = p => (p === '/admin' ? location.pathname === '/admin' : location.pathname.startsWith(p));
  const go         = p => { navigate(p); setMoreOpen(false); setUserOpen(false); };

  // "More" menu: everything not already a primary link, with its section
  // label — but drop labels whose items all went primary.
  const moreEntries = [];
  {
    let pendingSection = null;
    navItems.forEach(item => {
      if (item.section) { pendingSection = item.section; return; }
      if (primaryIds.has(item.id)) return;
      if (pendingSection) { moreEntries.push({ section: pendingSection }); pendingSection = null; }
      moreEntries.push(item);
    });
  }

  return (
    <header className="web-header">
      <button className="topbar-icon-btn web-hamburger" onClick={onMenuToggle} aria-label="Open menu">
        <IcMenuHamburger size={16} />
      </button>
      <div className="web-brand" onClick={() => go(linkItems[0]?.path || '/feed')}>
        <PlatformLogo size={30} />
        <span className="web-brand-name">Toppers Tutorial</span>
      </div>
      {title && <span className="web-header-title">{title}</span>}
      <nav className="web-header-links">
        {primary.map(item => (
          <button key={item.id} className={`web-link${isActive(item.path) ? ' active' : ''}`} onClick={() => go(item.path)}>
            {item.label}
            {item.badge && badges[item.badge] > 0 && <span className="web-link-dot" />}
          </button>
        ))}
        {moreEntries.length > 0 && (
          <div ref={moreRef} style={{ position: 'relative' }}>
            <button className={`web-link${moreOpen ? ' active' : ''}`} onClick={() => setMoreOpen(o => !o)}>
              More
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
            </button>
            {moreOpen && (
              <div className="web-menu">
                {moreEntries.map((e, i) => e.section
                  ? <div key={`s-${i}`} className="web-menu-sec">{e.section}</div>
                  : (
                    <button key={e.id} className={`web-menu-item${isActive(e.path) ? ' active' : ''}`} onClick={() => go(e.path)}>
                      <e.Icon size={14} /> {e.label}
                      {e.badge && badges[e.badge] > 0 && <span className="web-link-dot" />}
                    </button>
                  ))}
              </div>
            )}
          </div>
        )}
      </nav>
      <div style={{ flex: 1 }} />
      {!isAdmin && <GlobalSearch />}
      {actions}
      {!isAdmin && !isWriter && (
        <button className="topbar-icon-btn" aria-label="Notifications" onClick={() => go('/notifications')}>
          <IcBell size={14} />{badges.notifs > 0 && <span className="topbar-notif-dot" />}
        </button>
      )}
      <div ref={userRef} style={{ position: 'relative' }}>
        <div className="topbar-avatar" onClick={() => setUserOpen(o => !o)}>
          {currentUser?.profilePicture ? <img src={currentUser.profilePicture} alt={avatarInitials} /> : avatarInitials}
        </div>
        {userOpen && (
          <div className="web-menu web-menu--right">
            <div className="web-menu-user">{displayName}</div>
            <button className="web-menu-item" onClick={() => go('/account')}><IcProfile size={14} /> Account</button>
            <button className="web-menu-item" onClick={toggleDark}><IcSettings size={14} /> {dark ? 'Light mode' : 'Dark mode'}</button>
            <button className="web-menu-item" onClick={() => { logout(); navigate('/'); }}><IcLogout size={14} /> Sign out</button>
          </div>
        )}
      </div>
    </header>
  );
}

export function Topbar({ title, subtitle, actions, onMenuToggle, badges = {} }) {
  const navigate = useNavigate();
  const { currentUser, avatarInitials, isAdmin, isWriter } = useAuth();
  return (
    <div className={`topbar${isAdmin ? ' topbar--admin' : ''}`}>
      <div className="topbar-left">
        <button className="topbar-hamburger" onClick={onMenuToggle} aria-label="Open menu"><IcMenuHamburger size={18} /></button>
        <div className="topbar-title">{title}</div>
        {subtitle && <span className="topbar-badge">{subtitle}</span>}
      </div>
      <div className="topbar-right">
        {!isAdmin && <GlobalSearch />}
        {actions}
        {!isAdmin && !isWriter && (
          <>
            <button className="topbar-icon-btn" aria-label="Notifications" onClick={() => navigate('/notifications')}>
              <IcBell size={14} />{badges.notifs > 0 && <span className="topbar-notif-dot" />}
            </button>
            <button className="topbar-icon-btn" aria-label="Messages" onClick={() => navigate('/messages')}>
              <IcMsg size={14} />{badges.messages > 0 && <span className="topbar-notif-dot" />}
            </button>
          </>
        )}
        <div className="topbar-avatar" onClick={() => navigate('/account')}>
          {currentUser?.profilePicture ? <img src={currentUser.profilePicture} alt={avatarInitials} /> : avatarInitials}
        </div>
      </div>
    </div>
  );
}

function BottomNav({ onMenuOpen }) {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { isStudent, isAdmin, isWriter, isLoader } = useAuth();
  const { badges } = useBadges();

  const adminItems = [
    { path: '/admin',               label: 'Dashboard',  Icon: IcDashboard, FI: FiDashboard },
    { path: '/admin/students-hub',  label: 'Students',   Icon: IcUsers,     FI: FiUsers },
    { path: '/admin/comms',         label: 'Comms',      Icon: IcBroadcast, FI: FiBroadcast, count: badges.notifs },
    { path: '/admin/community',     label: 'Community',  Icon: IcFeed,      FI: FiHome },
  ];

  const writerItems = [
    { path: '/news/write', label: 'Write',    Icon: IcNewspaper, FI: FiNewspaper },
    { path: '/news',       label: 'News Hub', Icon: IcFeed,      FI: FiHome },
    { path: '/account',    label: 'Account',  Icon: IcProfile,   FI: FiProfile },
  ];

  const userItems = isStudent ? [
    { path: '/feed',          label: 'Home',      Icon: IcFeed,      FI: FiHome  },
    { path: '/learn',         label: 'Learn',     Icon: IcCourses,   FI: FiLearn, count: badges.assign },
    { path: '/messages',      label: 'Chats',     Icon: IcMsg,       FI: FiChat,  count: badges.messages },
    { path: '/notifications', label: 'Notifs',    Icon: IcBell,      FI: FiBell,  count: badges.notifs   },
  ] : [
    { path: '/tutor-dashboard', label: 'Dashboard', Icon: IcDashboard, FI: FiDashboard },
    { path: '/messages',      label: 'Messages',  Icon: IcMsg,       FI: FiChat,  count: badges.messages },
    { path: '/groups',        label: 'Groups',    Icon: IcGroup,     FI: FiGroup },
    { path: '/notifications', label: 'Notifs',    Icon: IcBell,      FI: FiBell,  count: badges.notifs   },
  ];

  const loaderItems = [
    { path: '/loader-console', label: 'Loader',   Icon: IcUpload   },
    { path: '/account',        label: 'Account',  Icon: IcProfile  },
  ];

  const items = isAdmin ? adminItems : isWriter ? writerItems : isLoader ? loaderItems : userItems;
  const navBg = isAdmin ? '#0D1117' : 'var(--white)';
  const borderC = isAdmin ? 'rgba(255,255,255,0.08)' : 'var(--nat-d)';

  // Auto-hide on scroll-down, reveal on scroll-up (WhatsApp/Material behavior).
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);
  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY || document.documentElement.scrollTop || 0;
        const dy = y - lastY.current;
        if (y < 60) setHidden(false);                 // always show near the top
        else if (dy > 8) setHidden(true);             // scrolling down → hide
        else if (dy < -8) setHidden(false);           // scrolling up → reveal
        lastY.current = y;
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  // Reveal whenever the route changes so a fresh page never opens with it hidden.
  useEffect(() => { setHidden(false); lastY.current = 0; }, [location.pathname]);

  const navStyle = { display: 'none', position: 'fixed', bottom: 0, left: 0, right: 0, background: navBg, borderTop: `1px solid ${borderC}`, zIndex: 200, alignItems: 'stretch', justifyContent: 'space-around', paddingTop: 6, paddingBottom: 'max(6px, env(safe-area-inset-bottom))', transform: hidden ? 'translateY(110%)' : 'translateY(0)', transition: 'transform 0.28s cubic-bezier(0.4,0,0.2,1)' };
  const inactiveC = isAdmin ? 'rgba(255,255,255,0.5)' : 'var(--txt-l)';
  const pillBg    = isAdmin ? 'rgba(255,106,0,0.22)' : 'var(--org-l)';

  // WhatsApp / Material-3 nav item: a capsule "indicator" slides in behind the
  // active icon, the icon fills + switches to the brand colour, and a quick
  // press-scale gives tactile feedback.
  const NavBtn = ({ label, Icon, FI, count, isActive, onClick }) => {
    const ActiveIcon = (isActive && FI) ? FI : Icon;
    return (
      <button onClick={onClick}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, flex: 1, padding: '2px 0', border: 'none', background: 'transparent', cursor: 'pointer', minHeight: 50, transition: 'transform 0.1s' }}
        onTouchStart={e => { e.currentTarget.style.transform = 'scale(0.92)'; }}
        onTouchEnd={e => { e.currentTarget.style.transform = 'none'; }}>
        <span style={{
          position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          height: 30, minWidth: 58, borderRadius: 999,
          background: isActive ? pillBg : 'transparent',
          transition: 'background 0.22s cubic-bezier(0.4,0,0.2,1)',
        }}>
          <ActiveIcon size={21} color={isActive ? 'var(--org)' : inactiveC} />
          {count > 0 && (
            <span style={{ position: 'absolute', top: -3, right: 6, background: 'var(--org)', color: '#fff', borderRadius: 99, minWidth: 15, height: 15, fontSize: 9, fontWeight: 800, lineHeight: '15px', textAlign: 'center', padding: '0 3px', border: `1.5px solid ${navBg}` }}>{count > 99 ? '99+' : count}</span>
          )}
        </span>
        <span style={{ fontSize: 10.5, fontWeight: isActive ? 800 : 600, color: isActive ? (isAdmin ? '#fff' : 'var(--org)') : inactiveC, transition: 'color 0.15s' }}>{label}</span>
      </button>
    );
  };

  return (
    <nav style={navStyle} className="bottom-nav">
      {items.map(({ path, label, Icon, FI, count }) => {
        let active = location.pathname === path || (path !== '/admin' && location.pathname.startsWith(path + '/'));
        // Chats tab stays lit across both chat surfaces (Messages ⇄ Groups).
        if (label === 'Chats' && location.pathname.startsWith('/groups')) active = true;
        const isActive = isAdmin ? (location.pathname === path) : active;
        return <NavBtn key={path} label={label} Icon={Icon} FI={FI} count={count} isActive={isActive} onClick={() => navigate(path)} />;
      })}
      <NavBtn label="Menu" Icon={IcMenuHamburger} isActive={false} onClick={onMenuOpen} />
    </nav>
  );
}

function InstallBanner({ onInstall, onDismiss }) {
  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9998,
      background: 'linear-gradient(135deg, #0c1428 0%, #1a2540 100%)',
      borderTop: '1px solid rgba(255,106,0,0.25)',
      padding: '14px 18px',
      display: 'flex', alignItems: 'center', gap: 14,
      boxShadow: '0 -4px 24px rgba(0,0,0,0.25)',
      animation: 'slideUpIn 0.3s ease',
    }}>
      <style>{`@keyframes slideUpIn{from{transform:translateY(100%);opacity:0}to{transform:translateY(0);opacity:1}}`}</style>
      {/* Icon */}
      <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--org)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: 'var(--display)', fontSize: 22, fontWeight: 900, color: '#fff' }}>T</div>
      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: '#fff', fontSize: 13.5, fontWeight: 700, lineHeight: 1.3 }}>Add to Home Screen</div>
        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11.5, marginTop: 2 }}>Get the full app experience — works offline too</div>
      </div>
      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        <button onClick={onDismiss} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, color: 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: '8px 12px' }}>Not now</button>
        <button onClick={onInstall} style={{ background: 'var(--org)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', padding: '8px 16px', boxShadow: '0 2px 10px rgba(255,106,0,0.4)' }}>Install</button>
      </div>
      {/* X */}
      <button onClick={onDismiss} style={{ position: 'absolute', top: 8, right: 8, background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)', cursor: 'pointer', padding: 4, display: 'flex' }}>
        <IcX size={14} />
      </button>
    </div>
  );
}

export function PageShell({ title, subtitle, actions, badges: propBadges, children }) {
  const [scrollTop, setScrollTop]           = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location   = useLocation();
  const contentRef = useRef(null);
  const { badges: ctxBadges } = useBadges();
  const effectiveBadges = propBadges || ctxBadges || {};
  const { showBanner, install, dismiss } = useInstallPrompt();

  useFaviconBadge();

  // Website mode: the browser gets site chrome (top navbar, centered content,
  // footer, no app gestures); the Capacitor shell keeps the app chrome.
  const web    = !isNativeApp();
  const isChat = ['/messages', '/groups'].some(p => location.pathname.startsWith(p));

  // Per-page browser-tab title ("Take Quiz — Toppers Tutorial"), a basic
  // website expectation the app shell never needed.
  useEffect(() => {
    const prev = document.title;
    document.title = title ? `${title} — Toppers Tutorial` : 'Toppers Tutorial';
    return () => { document.title = prev; };
  }, [title]);

  return (
    <div className={`app-shell${web ? ' app-shell--web' : ''}`}>
      {mobileMenuOpen && <div className="mobile-drawer-backdrop" onClick={() => setMobileMenuOpen(false)} />}
      <Sidebar badges={effectiveBadges} mobileOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />
      <div className="page-main">
        {web
          ? <WebHeader title={title} actions={actions} badges={effectiveBadges} onMenuToggle={() => setMobileMenuOpen(p => !p)} />
          : <Topbar title={title} subtitle={subtitle} actions={actions} badges={effectiveBadges} onMenuToggle={() => setMobileMenuOpen(p => !p)} />}
        <div className="page-content" ref={contentRef} onScroll={() => { if (contentRef.current) setScrollTop(contentRef.current.scrollTop); }}>
          {!window.location.pathname.startsWith('/subscription') && <SubscriptionReminder />}
          <div className={`page-enter${web && !isChat ? ' web-page-width' : ''}`} key={location.pathname}>
            {/* Website page heading — the app showed the title in its topbar;
                the site navbar doesn't, so pages identify themselves here. */}
            {web && !isChat && title && (
              <div className="web-page-heading">
                <h1>{title}</h1>
                {subtitle && <span className="web-page-sub">{subtitle}</span>}
              </div>
            )}
            {children}
          </div>
          {/* Website-only footer; chat surfaces are full-height and keep it off. */}
          {!isChat && <SiteFooter />}
        </div>
      </div>
      {!web && <BottomNav onMenuOpen={() => setMobileMenuOpen(true)} />}
      <ProfileReminder />
      {scrollTop > 300 && (
        <button onClick={() => contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{ position: 'fixed', bottom: showBanner ? 140 : 76, right: 20, zIndex: 150, width: 40, height: 40, borderRadius: '50%', background: 'var(--org)', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 16px rgba(255,165,0,0.35)', transition: 'bottom 0.25s' }}
          title="Back to top">
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
        </button>
      )}
      {showBanner && <InstallBanner onInstall={install} onDismiss={dismiss} />}
    </div>
  );
}
